import { useEffect, useCallback, useRef, useState } from 'react';
import mqtt from 'mqtt';

export const useMqttSubscription = (topics, onMessage, options = {}) => {
  const monitoring_user = process.env.REACT_APP_MONITORING_USER;
  const monitoring_key = process.env.REACT_APP_MONITORING_KEY;
  const mqtt_connection = process.env.REACT_APP_MQTT_CONNECTION;
  
  const clientRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const lastConnectAttempt = useRef(0);
  const connectionInProgress = useRef(false);
  const debugMode = false;
  
  // Helper to log if debug is enabled
  const log = useCallback((...args) => {
    if (debugMode) console.log(...args);
  }, [debugMode]);
  
  // Convert topics to array and filter out empty ones
  const getValidTopics = useCallback(() => {
    if (!topics) return [];
    const topicsArray = Array.isArray(topics) ? topics : [topics];
    return topicsArray.filter(t => t && typeof t === 'string' && t.trim() !== '');
  }, [topics]);
  
  // Check if we have valid topics to subscribe to
  const hasValidTopics = useCallback(() => {
    const validTopics = getValidTopics();
    const result = validTopics.length > 0;
    if (!result && debugMode) {
      log('No valid topics to subscribe to:', topics);
    }
    return result;
  }, [getValidTopics, log, topics, debugMode]);
  
  // Safely disconnect client
  const safeDisconnect = useCallback(() => {
    const client = clientRef.current;
    if (!client) return Promise.resolve();
    
    log('Safely disconnecting MQTT client...');
    
    return new Promise((resolve) => {
      try {
        client.end(true, {}, () => {
          log('MQTT client safely disconnected');
          clientRef.current = null;
          setIsConnected(false);
          connectionInProgress.current = false;
          resolve();
        });
        
        // Fallback if callback doesn't fire
        setTimeout(() => {
          if (clientRef.current === client) {
            log('Disconnect callback timeout - forcing disconnect');
            clientRef.current = null;
            setIsConnected(false);
            connectionInProgress.current = false;
          }
          resolve();
        }, 1000);
      } catch (e) {
        console.error('Error during safe disconnect:', e);
        clientRef.current = null;
        setIsConnected(false);
        connectionInProgress.current = false;
        resolve();
      }
    });
  }, [log]);
  
  // Connect to MQTT broker
const connect = useCallback(async () => {
  // Skip if already connecting
  if (connectionInProgress.current) {
    log('Connection already in progress, not connecting again');
    return;
  }
  
  // Check if we should connect (rate limiting)
  const now = Date.now();
  if (now - lastConnectAttempt.current < 5000) {
    log('Throttling connect attempt, too soon after last attempt');
    return;
  }
  
  // Check if we have topics to subscribe to
  if (!hasValidTopics()) {
    log('No valid topics to subscribe to, not connecting');
    return;
  }
  
  // Check if we have connection details
  if (!mqtt_connection) {
    console.error('MQTT connection string not defined');
    return;
  }
  
  // Set flags
  connectionInProgress.current = true;
  lastConnectAttempt.current = now;
  
  // Clean up existing client if needed
  if (clientRef.current) {
    log('Existing client found, disconnecting first');
    await safeDisconnect();
  }
  
  try {
    // Create client ID
    const clientId = `monitor_${Math.random().toString(16).substring(2, 8)}`;
    log(`Creating new MQTT client with ID: ${clientId}`);
    
    // Connect with robust options
    const client = mqtt.connect(mqtt_connection, {
      clientId,
      username: monitoring_user,
      password: monitoring_key,
      clean: true,
      keepalive: 30,
      reconnectPeriod: 3000,
      connectTimeout: 30000,
      rejectUnauthorized: false,
      ...options
    });
      
      // Store reference immediately
      clientRef.current = client;
      
      // Set up event handlers
      client.on('connect', () => {
        log('✅ MQTT client connected');
        setIsConnected(true);
        connectionInProgress.current = false;
        
        // Subscribe to topics
        const validTopics = getValidTopics();
        log(`Subscribing to ${validTopics.length} topics:`, validTopics);
        
        validTopics.forEach(topic => {
          client.subscribe(topic, { qos: 0 }, (err) => {
            if (err) {
              console.error(`Error subscribing to ${topic}:`, err);
            } else {
              log(`✅ Subscribed to ${topic}`);
            }
          });
        });
      });
      
      client.on('message', (topic, message, packet) => {
        try {
          onMessage(topic, message, packet);
        } catch (error) {
          console.error('Error in message handler:', error);
        }
      });
      
      client.on('error', (err) => {
        console.error('MQTT client error:', err);
        setIsConnected(false);
      });
      
      client.on('close', () => {
        log('MQTT connection closed');
        setIsConnected(false);
      });
      
      client.on('offline', () => {
        log('MQTT client is offline');
        setIsConnected(false);
      });
      
      client.on('reconnect', () => {
        log('MQTT client attempting to reconnect');
      });
      
    } catch (error) {
      console.error('Error setting up MQTT client:', error);
      setIsConnected(false);
      connectionInProgress.current = false;
      clientRef.current = null;
    }
  }, [monitoring_user, monitoring_key, mqtt_connection, options, safeDisconnect, getValidTopics, hasValidTopics, log, onMessage]);
  
  // Connect when component mounts or topics change
  useEffect(() => {
    // Only connect if we have topics
    if (hasValidTopics()) {
      log('Topics changed, connecting with topics:', getValidTopics());
      connect();
    } else {
      log('No valid topics, skipping connection');
    }
    
    // Clean up on unmount
    return () => {
      log('Component unmounting, disconnecting...');
      safeDisconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(getValidTopics())]); // Use JSON.stringify to compare array contents
  
  return {
    isConnected,
    disconnect: safeDisconnect,
    reconnect: connect
  };
};